import { useMemo } from 'react';
import { useAuth } from '../features/auth/useAuth';

/**
 * Permission-aware UI helpers. The UI must never offer an action the role
 * lacks — the backend is still the real guard (CLAUDE.md rule 3).
 */

/**
 * The permission codes this app references, mirroring the backend catalogue
 * in `backend/src/shared/permissions.ts` (design.md §2.5). Code is the source
 * of truth on both sides; a literal string in a component is a review
 * rejection.
 *
 * There is deliberately no `user.read`/`role.read` — read and write share one
 * code in **feature 001** (001/design.md G-5).
 *
 * **Feature 002 splits read from manage** (002/design.md §2.5), and that split
 * is load-bearing rather than cosmetic: an Auditor must read stock without
 * being able to post a receipt, and a Store Keeper must pick a vendor without
 * being able to edit the vendor master. `*.manage` does **not** imply `*.read`,
 * on either side — a screen guard asks for the read code, an action asks for
 * the manage code, and a manage-only grant is a misconfigured role.
 */
export const PERMISSIONS = {
  // --- feature 001
  USER_MANAGE: 'user.manage',
  USER_RESET_PASSWORD: 'user.reset_password',
  ROLE_MANAGE: 'role.manage',

  // --- feature 002 (002/design.md §2.5). Eleven codes, spelled EXACTLY as
  // `backend/src/shared/permissions.ts`. A typo here is a silent 403 on one
  // side only: the button renders and the request is refused, or the button
  // hides for a role that was in fact granted the code.
  PLANT_PROFILE_READ: 'plant_profile.read',
  PLANT_PROFILE_MANAGE: 'plant_profile.manage',
  RAW_MATERIAL_READ: 'raw_material.read',
  RAW_MATERIAL_MANAGE: 'raw_material.manage',
  VENDOR_READ: 'vendor.read',
  VENDOR_MANAGE: 'vendor.manage',
  TRANSPORTER_READ: 'transporter.read',
  TRANSPORTER_MANAGE: 'transporter.manage',
  GOODS_RECEIPT_READ: 'goods_receipt.read',

  // --- feature 011. Spelled EXACTLY as `backend/src/shared/permissions.ts`.
  PAYMENT_READ: 'payment.read',
  PAYMENT_CREATE: 'payment.create',
  /**
   * Stories G–K (gk-build-plan.md §3.2 W0). `payment.reverse` is its own grant — it implies
   * neither `payment.read` nor `payment.create`. `party_ledger.read` gates the Ledger tab and the
   * balance fields on the vendor and transporter screens; without it the server omits those keys.
   */
  PAYMENT_REVERSE: 'payment.reverse',
  PARTY_LEDGER_READ: 'party_ledger.read',
  /**
   * The payment form's two pickers read these masters' list endpoints, and the backend gates those
   * on their own `*.read` codes — not on `payment.create` as brief §5's surface table intends. The
   * form checks them so a recorder without them is told why a picker is empty rather than shown a
   * silent 403.
   */
  ACCOUNT_HEAD_READ: 'account_head.read',
  PAYMENT_METHOD_READ: 'payment_method.read',
  /** The two master screens' writes (011 F4/F5). `*.manage` does not imply `*.read`. */
  ACCOUNT_HEAD_MANAGE: 'account_head.manage',
  PAYMENT_METHOD_MANAGE: 'payment_method.manage',
  GOODS_RECEIPT_CREATE: 'goods_receipt.create',
  STOCK_READ: 'stock.read',

  /*
   * --- feature 012: the goods-receipt lifecycle (012/design.md §2.9, §9.1 task S-3).
   * Spellings copied from `backend/src/shared/permissions.ts`, not retyped.
   *
   * These are **independent grants, not a ladder**: `goods_receipt.create` implies none of
   * them, because being trusted to record what arrived is not the same authority as being
   * trusted to restate or annul it afterwards. A role that should do both holds both.
   *
   * There is deliberately **no `goods_receipt.print`** — Print renders exactly what
   * `goods_receipt.read` already authorises.
   */
  GOODS_RECEIPT_EDIT: 'goods_receipt.edit',
  GOODS_RECEIPT_VOID: 'goods_receipt.void',
  PURCHASE_RETURN_CREATE: 'purchase_return.create',
  PURCHASE_RETURN_READ: 'purchase_return.read',
  PURCHASE_RETURN_VOID: 'purchase_return.void',

  /*
   * --- feature 010 (010/design.md §5.0). **One pair, not two**: a tax
   * component has no standalone meaning, and splitting read/manage per entity
   * would create a role that can author a "CGST 6%" row and never use it.
   *
   * `tax_config.read` alone renders both lists with **no Create, Edit,
   * Publish or Deactivate control**; the write endpoints answer 403 either
   * way. Holding neither code is a 403 on read, which is why the nav item
   * carries the read code and a Store Keeper never sees it.
   */
  TAX_CONFIG_READ: 'tax_config.read',
  TAX_CONFIG_MANAGE: 'tax_config.manage',

  /*
   * --- feature 003: finished goods and recipes (003/design.md §4.3, ADR-0038).
   * Spellings copied from `backend/src/shared/permissions.ts`, not retyped.
   *
   * **Split from `raw_material.*` over ONE `items` table**, and the split is the product
   * requirement rather than tidiness: a Sales Executive must read the block master to quote and
   * must never edit fly ash, and a Production Supervisor must read the mix and must not retune
   * it. One `item.*` code would let whoever can sell a block edit the raw-material master.
   *
   * The visible consequence is the asymmetry on the nav: a Sales Executive sees **Finished Goods
   * and not Recipes** (brief D-6). That is the requirement made legible, not an oversight.
   *
   * These codes do not make the split real on their own — a route constant cannot know the TYPE
   * of the row it is about to write. The server's `guardItemTypeAuthority` and the required
   * `itemTypes` predicate on every item read are what enforce it; these are the names those
   * mechanisms resolve against. `*.manage` does not imply `*.read`, as everywhere else here.
   */
  FINISHED_GOOD_READ: 'finished_good.read',
  FINISHED_GOOD_MANAGE: 'finished_good.manage',
  RECIPE_READ: 'recipe.read',
  RECIPE_MANAGE: 'recipe.manage',

  /*
   * --- feature 016: moulds and the manufacturing plan (016/design.md §4.1).
   * Spellings copied from `backend/src/shared/permissions.ts` lines 157-160, not retyped.
   *
   * **Create / confirm / cancel are deliberately NOT split.** 012 split `goods_receipt.void`
   * from `create` because annulling a *financial document* is a different privilege from
   * raising one; cancelling a plan destroys no stock, no money and no statutory record, so the
   * same split here would be ceremony. There is likewise no `manufacturing_plan.approve` and no
   * `pending_approval` status — the manager explicitly chose the open path, and a gate added
   * later would reverse that choice rather than improve on it (§0.9).
   *
   * **Three codes that must NOT be required anywhere in these two screens**, each a failing
   * criterion if it creeps in (Story H):
   *   - `stock.read` for the shortfall and on-hand figures — the shortfall is part of the plan;
   *   - `recipe.read` for the material requirement, recipe name, version and per-m³ figures;
   *   - `mould.read` for the pour count, the mould picker or the geometry a plan used.
   * That last one is why the plan module serves its own three option feeds under
   * `manufacturing_plan.manage` instead of reusing 003's pickers.
   */
  /*
   * 021 re-described these two (no new codes, D-6): `mould.read` views and `mould.manage` edits
   * the **plant mould setting** (`/settings/mould`), which replaced 016's per-mould master. Plans
   * still need neither — P8 serves the mould summary under `manufacturing_plan.read`.
   */
  MOULD_READ: 'mould.read',
  MOULD_MANAGE: 'mould.manage',
  MANUFACTURING_PLAN_READ: 'manufacturing_plan.read',
  MANUFACTURING_PLAN_MANAGE: 'manufacturing_plan.manage',

  /*
   * --- feature 005: production consumption (005/design.md §5.1, §9.1 task F-*).
   * Spellings copied from `backend/src/shared/permissions.ts` lines 290-292, not retyped.
   *
   * **Three independent grants, and none implies another** (§5.1): a Production Supervisor
   * can create and cannot list (`production_run.create` alone); a `production_run.reverse`
   * holder does not get `read` as a side-effect. A route guard asks for the read code, a Post
   * button asks for create, a Reverse button asks for reverse.
   */
  PRODUCTION_RUN_READ: 'production_run.read',
  PRODUCTION_RUN_CREATE: 'production_run.create',
  PRODUCTION_RUN_REVERSE: 'production_run.reverse',

  /*
   * --- feature 020: the top-header cost calculator (020/design.md §4.1, task S-2).
   * Spelling copied from `backend/src/shared/permissions.ts`, not retyped.
   *
   * **One code, and it gates the surface, not each figure.** It is sufficient on its own for
   * both directions: `stock.read`, `recipe.read`, `finished_good.read`, `mould.read` and every
   * purchase code must NOT be required anywhere in the panel (brief §5, Story H). The one
   * conjunction is the plan path — picking an existing plan as the input also needs
   * `manufacturing_plan.read`, and without it the plan picker is absent.
   *
   * It discloses purchase rates and landed costs, which is why it is not in a Store Keeper's
   * recommended grant. There is no `.manage`: the calculator writes nothing.
   */
  COST_CALCULATOR_USE: 'cost_calculator.use',
  /*
   * --- feature 004: purchase orders (004/design.md §2.9).
   * Spellings copied from `backend/src/shared/permissions.ts` lines 110-114, not retyped.
   *
   * **Five independent grants, not a ladder** (AC-L3): none implies another, none is implied by
   * `goods_receipt.*`, and every read endpoint requires `purchase_order.read` explicitly. A
   * maker-checker split is achieved by NOT granting `create` and `approve` together — there is
   * deliberately no creator-/=-approver check in the code, and AC-B3 requires that said plainly
   * rather than implied.
   *
   * `purchase_order.close` grants close, **reopen and cancel**: ending an order is one authority.
   *
   * There is deliberately **no `purchase_order.match` and no `purchase_order.print`** — each
   * would gate something `goods_receipt.create` + `purchase_order.read` already gate, and a
   * permission that gates nothing is worse than no permission (012's refused `goods_receipt.print`).
   */
  PURCHASE_ORDER_READ: 'purchase_order.read',
  PURCHASE_ORDER_CREATE: 'purchase_order.create',
  PURCHASE_ORDER_APPROVE: 'purchase_order.approve',
  PURCHASE_ORDER_AMEND: 'purchase_order.amend',
  PURCHASE_ORDER_CLOSE: 'purchase_order.close',

  /*
   * --- feature 013: the documents attached to a goods receipt (013/design.md §2.8, task F1a).
   * Spellings copied from `backend/src/shared/permissions.ts`, not retyped.
   *
   * **Three independent grants, and each one ALSO requires `goods_receipt.read`.** That
   * conjunction is new to this app's UI and it **refuses rather than narrows**: holding
   * `goods_receipt_attachment.read` without `goods_receipt.read` is a 403 from the server in
   * both directions (AC-G2), so the UI must ask for both with `canAll` and must not fall back to
   * "well, they hold the attachment code, show it anyway". `usePermission` on one code alone is
   * the mistake this comment exists to prevent.
   *
   * **None of the three implies another, and `goods_receipt.read`/`.create`/`.edit` grant none
   * of them** (AC-L3). `…create` without `…read` is a real and intended configuration — a gate
   * clerk who may photograph a weighbridge slip and may not browse the vendor invoice images —
   * which is why an upload's own 201 response carries `canView: false` for that user and why the
   * list has a redacted branch rather than being hidden.
   *
   * **Deliberately scoped to the goods receipt, not a generic `document_attachment.*`** (brief
   * §4): a generic grant made today for inbound receipts would silently cover customer sales
   * documents the day feature 006 ships. Extending to a purchase return is a fourth code and a
   * route, not a widening of these.
   *
   * There is deliberately **no permission for the weights**: they are read with
   * `goods_receipt.read`, entered with `.create` and corrected with `.edit`.
   */
  GOODS_RECEIPT_ATTACHMENT_CREATE: 'goods_receipt_attachment.create',
  GOODS_RECEIPT_ATTACHMENT_READ: 'goods_receipt_attachment.read',
  GOODS_RECEIPT_ATTACHMENT_DELETE: 'goods_receipt_attachment.delete',

  /**
   * --- feature 025 (025/design.md §2.4(c), brief §4). **Four codes, and only
   * four:** the `vendor_bill.*` codes belong to feature 026 and are
   * deliberately not here — an unused code in the register is a permission
   * that gates nothing, which is the rule `permissions.ts:48-50` states.
   *
   * **Independent grants, never a ladder** (012's convention): `.create`
   * grants neither `.read`, `.edit` nor `.void`. The recommended role map
   * gives a Plant Manager `.edit|.void` **without** `.create`, so any UI that
   * treats `.create` as the base grant would hide the correction path from the
   * one role that owns it.
   *
   * `.read` also gates the open-challan register and the challan tabs on the
   * Goods Receipts page (AC-D7): without it those tabs are **not rendered at
   * all**, and the API answers 403 regardless.
   *
   * There is deliberately **no `delivery_challan.print`** — printing renders
   * exactly what `.read` authorises — and **no attachment triple**: a challan
   * carries no price, so the confidentiality 013's third code protects does
   * not exist on this document (design §4.6, D-D).
   */
  DELIVERY_CHALLAN_READ: 'delivery_challan.read',
  DELIVERY_CHALLAN_CREATE: 'delivery_challan.create',
  DELIVERY_CHALLAN_EDIT: 'delivery_challan.edit',
  DELIVERY_CHALLAN_VOID: 'delivery_challan.void',

  /**
   * --- feature 026 (026/design.md §4.0, §7.1; brief §4). **Four codes, and
   * only four** — the ones 025 deliberately did not add, with the comment
   * above saying so.
   *
   * **Independent grants, never a ladder:** `.create` grants neither `.read`,
   * `.edit` nor `.void`, and holding `.create` **and** `.edit` is still a 403
   * on the void (AC-J6). The recommended map gives a Plant Manager every
   * `.read` plus `vendor_bill.void` and no `.create`, so any UI treating
   * `.create` as the base grant would hide the void from the role that owns it.
   *
   * `.read` also gates **every reference to a bill anywhere else**: the Vendor
   * bills tab on the inbound strip, the challan detail's settlement rows and
   * its revaluation history, the stock ledger's `vendor_bill` source link and
   * 011's payment rows (AC-P6, AS-2 — the Store Keeper has no screen anywhere
   * that shows a purchase price).
   *
   * There is deliberately **no `vendor_bill.settle`** (allocation happens
   * inside create/edit, so it would gate nothing — D-12), **no
   * `vendor_bill.print`** (print renders exactly what `.read` authorises),
   * **no attachment triple** (D-D: the bill's documents reuse these four), **no
   * code for revaluation** (it has no route at all — AC-G10) and **no fifth
   * code for Story R** (it opens Story E's form and fills Story F's step, both
   * already gated by `.create`).
   */
  VENDOR_BILL_READ: 'vendor_bill.read',
  VENDOR_BILL_CREATE: 'vendor_bill.create',
  VENDOR_BILL_EDIT: 'vendor_bill.edit',
  VENDOR_BILL_VOID: 'vendor_bill.void',
} as const;

export type PermissionCode = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

/**
 * What the signed-in user may do, as the server reports it.
 *
 * **Both fields are required**, and that is the whole point. The super-admin
 * role holds **no** `role_permissions` rows — deliberately, so its access is
 * not mistaken for something enumerable (design.md §2.6) — so `GET /auth/me`
 * returns `permissions: []` for it while every endpoint still answers 200 via
 * the server-side bypass (§3.3).
 *
 * A UI that checked `permissions.includes(...)` alone would therefore hide
 * Users and Roles from the one account that is guaranteed to be able to use
 * them. `hasTotalAccess` is the session's mirror of `roles.is_super_admin`,
 * resolved from the live database row on every request (ADR-0013) — it is a
 * server-supplied fact, exactly like `permissions`, not a client-side
 * authority flag. The bypass itself still exists only on the server.
 */
export type Authority = {
  permissions: readonly string[];
  hasTotalAccess: boolean;
};

/** Does this user hold `code` — or bypass the check entirely? */
export function can(authority: Authority, code: string | undefined): boolean {
  if (code === undefined) return true;
  if (authority.hasTotalAccess) return true;
  return authority.permissions.includes(code);
}

/** At least one of `codes`. An empty list means "no permission required". */
export function canAny(authority: Authority, codes: readonly string[]): boolean {
  if (codes.length === 0) return true;
  if (authority.hasTotalAccess) return true;
  return codes.some((code) => authority.permissions.includes(code));
}

export function canAll(authority: Authority, codes: readonly string[]): boolean {
  if (authority.hasTotalAccess) return true;
  return codes.every((code) => authority.permissions.includes(code));
}

/** The current session's authority. */
export function useAuthority(): Authority {
  const { permissions, user } = useAuth();
  const hasTotalAccess = user?.hasTotalAccess ?? false;
  return useMemo(() => ({ permissions, hasTotalAccess }), [permissions, hasTotalAccess]);
}

/** Hook form of `can`, reading the current session. */
export function usePermission(code: string | undefined): boolean {
  return can(useAuthority(), code);
}
